import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { DynamicTranslationService } from '../shared/dynamic-translation/dynamic-translation.service';
import { UserFacade } from '../account/store/facades/user-facade';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { User } from '../user-management/users/user';
import { UsersService } from '../user-management/users/users.service';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  PasswordPolicyValidator,
  PasswordsDifferencesValidator
} from '../shared/custom-validator/passwords.validator';
import { AuthenticationService } from '../authentication/authentication.service';

@Component({
  selector: 'ls-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['../../assets/stylesheets/auth.scss']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  public emailToken: string;
  public forced = false;
  public passwordForm: FormGroup;
  public usernameForm: FormGroup;

  public error: string;
  public success: string;
  private ngUnsubscribe: Subject<Subscription> = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private translation: DynamicTranslationService,
    private userService: UsersService,
    private userFacade: UserFacade,
    private fb: FormBuilder,
    private authenticationService: AuthenticationService
  ) {}

  public ngOnInit(): void {
    combineLatest(this.activatedRoute.params, this.activatedRoute.data)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([param, data]: [Params, any]) => {
        this.forced = data.forced;
        this.emailToken = param['token'];
        if (this.forced || this.emailToken) {
          this.initPasswordForm();
        } else {
          this.initUsernameForm();
        }
      });
  }

  public logout() {
    this.authenticationService.logout();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public usernameFormSubmit(): void {
    if (!this.usernameForm.valid) {
      return;
    }

    const observable: Observable<
      any
    > = this.userService.sendEmailToResetPassword(
      this.usernameForm.value.username
    );
    this.send(observable);
  }

  public passwordFormSubmit(): void {
    if (!this.passwordForm.valid) {
      return;
    }
    const formValue: any = this.passwordForm.value;
    let observable: Observable<any>;
    if (this.forced) {
      observable = this.userFacade.getUserInformationFromStore().pipe(
        switchMap((user: User) => {
          return this.userService.resetPassword(
            user.id,
            formValue.oldPassword,
            formValue.password
          );
        }),
        takeUntil(this.ngUnsubscribe)
      );
    } else {
      observable = this.userService.resetPasswordFromEmail(
        this.emailToken,
        formValue.password
      );
    }
    this.send(observable);
  }

  private send(observable: Observable<any>): void {
    observable.subscribe(
      () => {
        this.success = this.getSuccessMessage();
      },
      (error: any) => {
        const exceptionErrorsToBeShown: Array<string> = [
          'No valid user found',
          'Invalid username!'
        ];
        if (
          error.code < 10000 &&
          exceptionErrorsToBeShown.indexOf(error.msg) === -1
        ) {
          error.msg = this.translation.translate(
            'Something went wrong. Please try again or contact Support.'
          );
        }
        this.error = error.msg;
      }
    );
  }

  private getSuccessMessage(): string {
    if (this.emailToken || this.forced) {
      return this.translation.translate(
        'Your password has been reset successfully! You can now login with your new password.'
      );
    }
    return this.translation.translate(
      'An email has been sent to you and should arrive shortly with password reset instructions. ' +
        'Please visit your email and click the provided link.'
    );
  }

  private initUsernameForm(): void {
    this.usernameForm = this.fb.group({
      username: ['', [Validators.required]]
    });
  }

  private initPasswordForm(): void {
    this.passwordForm = this.fb.group({
      oldPassword: [
        { value: '', disabled: !this.forced },
        [Validators.required]
      ],
      password: ['', [Validators.required, PasswordPolicyValidator]],
      confirmPassword: [
        '',
        [Validators.required, PasswordsDifferencesValidator]
      ]
    });
  }
}
